import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import resizeImage, { IMAGE_ACCEPT, isImageFile } from "@/lib/imageResize";
import { invalidateHomepageImages } from "@/lib/useHomepageImages";

const SLOTS = [
  { key: "residential_1", label: "Residential Image 1", hint: "Home page hero (top of /)" },
  { key: "residential_2", label: "Residential Image 2", hint: "Home page residential card" },
  { key: "commercial_1", label: "Commercial Image 1", hint: "Home page commercial card" },
  { key: "commercial_2", label: "Commercial Image 2", hint: "Commercial Cleaning page hero" },
];


export default function AdminHomepageImages() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSlot, setSavingSlot] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/homepage-images");
      const byKey = Object.fromEntries((r.data || []).map((it) => [it.slot, it]));
      setSlots(
        SLOTS.map((s) => ({
          ...s,
          ...(byKey[s.key] || { image_data: "", alt_text: "" }),
          saved_image_data: (byKey[s.key] || {}).image_data || "",
        })),
      );
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSlot = (key, patch) =>
    setSlots((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const onFile = async (key, file) => {
    if (!file) return;
    if (!isImageFile(file)) {
      toast.error("Please choose an image file.");
      return;
    }
    try {
      const compressed = await resizeImage(file);
      updateSlot(key, { image_data: compressed });
      toast.success("Photo ready — click Save to replace the stock photo.");
    } catch (e) {
      toast.error("Failed to read image.");
    }
  };

  const clearImage = (key) => updateSlot(key, { image_data: "" });

  const save = async (key) => {
    const slot = slots.find((s) => s.key === key);
    if (!slot) return;
    setSavingSlot(key);
    try {
      await api.put(`/admin/homepage-images/${key}`, {
        image_data: slot.image_data || "",
        alt_text: slot.alt_text || "",
      });
      invalidateHomepageImages();
      toast.success(
        slot.image_data
          ? `${slot.label} saved — your photo is now live on the website.`
          : `${slot.label} cleared — the stock photo is back.`,
      );
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingSlot(null);
    }
  };

  return (
    <div data-testid="admin-homepage-images">
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-[#0F172A] flex items-center gap-2">
          <ImageIcon className="w-6 h-6 text-[#1E3A8A]" /> Homepage Images
        </h2>
        <p className="mt-1 text-sm text-[#475569]">
          Upload Paula&apos;s real residential and commercial cleaning photos. A saved
          photo replaces the stock image on the website right away; clear a photo
          and save to bring the stock image back. Recommended size:
          1600×1000px. JPG, PNG or WebP up to ~4 MB before save. Photos are saved as WebP for faster pages.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[#475569]">Loading…</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {slots.map((s) => (
            <SlotCard
              key={s.key}
              slot={s}
              saving={savingSlot === s.key}
              onFile={(f) => onFile(s.key, f)}
              onAlt={(v) => updateSlot(s.key, { alt_text: v })}
              onClear={() => clearImage(s.key)}
              onSave={() => save(s.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SlotCard({ slot, saving, onFile, onAlt, onClear, onSave }) {
  const inputRef = useRef(null);
  const hasImage = !!slot.image_data;
  const unsaved = (slot.image_data || "") !== (slot.saved_image_data || "");
  const status = unsaved
    ? { text: "Not saved yet", cls: "bg-amber-50 text-amber-800 border-amber-200" }
    : slot.saved_image_data
      ? { text: "Live on website", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" }
      : { text: "Using stock photo", cls: "bg-slate-50 text-slate-700 border-slate-200" };

  return (
    <div
      data-testid={`hpi-slot-${slot.key}`}
      className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display font-semibold text-[#0F172A]">{slot.label}</p>
          <p className="text-xs text-[#475569] mt-0.5">{slot.hint}</p>
        </div>
        <span
          data-testid={`hpi-${slot.key}-status`}
          className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${status.cls}`}
        >
          {status.text}
        </span>
      </div>

      <div className="mt-4 aspect-[16/10] rounded-xl bg-slate-50 border border-dashed border-slate-200 overflow-hidden flex items-center justify-center">
        {hasImage ? (
          <img
            src={slot.image_data}
            alt={slot.alt_text || slot.label}
            data-testid={`hpi-${slot.key}-preview`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center text-xs text-slate-400 px-6">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No photo yet. The website shows the stock photo.
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`hpi-${slot.key}-upload`}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
        >
          <Upload className="w-4 h-4" /> Choose Image
        </button>
        {hasImage && (
          <button
            type="button"
            onClick={onClear}
            data-testid={`hpi-${slot.key}-clear`}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            <Trash2 className="w-4 h-4" /> Clear
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          data-testid={`hpi-${slot.key}-file-input`}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </div>

      <label className="mt-4 block">
        <span className="block text-xs font-medium text-[#0F172A]">Alt text</span>
        <input
          type="text"
          value={slot.alt_text || ""}
          onChange={(e) => onAlt(e.target.value)}
          placeholder="Describe the photo for screen readers + SEO"
          data-testid={`hpi-${slot.key}-alt`}
          className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
        />
      </label>

      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid={`hpi-${slot.key}-save`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E40AF] transition-colors disabled:opacity-60"
      >
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
